import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { DownloadOverlay, formatFileSize } from '../player/components/DownloadOverlay';
import { FullscreenButton } from '../player/components/FullscreenButton';
import { ViewerToolbarOverflowMenu } from '../player/components/ViewerToolbarOverflowMenu';
import { useContainerWidth } from '../player/hooks/useContainerWidth';
import { SidePanel } from '../player/components/SidePanel';
import { requestFullscreen, exitFullscreen as exitFs, getFullscreenElement, addFullscreenChangeListener, } from '../player/utils/fullscreenCompat';
import { getMarkerFillUri } from '../player/utils/markerSvg';
import { useVirtualLines } from './useVirtualLines';
import { useTooltip } from '../hooks/useTooltip';
const DEFAULT_CHUNK_SIZE = 256 * 1024; // 256 KB
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 32;
const DEFAULT_FONT_SIZE = 13;
const SCROLL_TRIGGER_PX = 600; // distance from bottom that triggers next chunk
const FULLSCREEN_CLASS = 'hekate-player-fullscreen-wrapper';
const TEXT_VIEWER_KEYBOARD_SHORTCUTS = [
    { key: '↑', description: 'Select previous line' },
    { key: '↓', description: 'Select next line' },
    { key: 'Home', description: 'Select first line' },
    { key: 'End', description: 'Select last loaded line' },
    { key: 'Page Up', description: 'Move selection up one page' },
    { key: 'Page Down', description: 'Move selection down one page' },
    { key: '-', description: 'Decrease font size' },
    { key: '=', description: 'Increase font size' },
    { key: 'Shift + F', description: 'Toggle fullscreen' },
];
/** Parse `Content-Range: bytes start-end/total` and return the total. */
function totalBytesFromContentRange(header) {
    if (!header)
        return null;
    const m = /\/(\d+)\s*$/.exec(header.trim());
    if (!m)
        return null;
    const n = parseInt(m[1], 10);
    return Number.isFinite(n) && n > 0 ? n : null;
}
/**
 * Streaming UTF-8 decoder for chunked range responses.
 *
 * Wraps `TextDecoder` so callers can feed the raw bytes of each range
 * response and get text out without losing characters when a multi-byte
 * codepoint is split across two chunks. The native `{ stream: true }` option
 * already handles this — we just keep one decoder alive across the whole load.
 */
function makeStreamingDecoder() {
    const decoder = new TextDecoder('utf-8');
    return {
        feed: (chunk, last) => decoder.decode(chunk, { stream: !last }),
    };
}
/**
 * Streaming text-file previewer. Loads the file in chunks via HTTP Range
 * requests so multi-MB log files don't hang the tab, and offers a small
 * toolbar (line numbers, word-wrap, copy, font-size).
 */
const HekateTextViewer = forwardRef(function HekateTextViewer(props, ref) {
    const { fileUrl, fileTitle, fileSize, width, height, chunkSize = DEFAULT_CHUNK_SIZE, initialWordWrap = true, initialLineNumbers = true, downloadUrl, fileTypeName, fileIconUrl, onFullscreenChange, sidePanelElementId, sidePanelWidth: sidePanelWidthProp, sidePanelTooltip, } = props;
    const [text, setText] = useState('');
    const [loadedBytes, setLoadedBytes] = useState(0);
    const [totalBytes, setTotalBytes] = useState(fileSize > 0 ? fileSize : 0);
    const [isLoadingChunk, setIsLoadingChunk] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [fullyLoaded, setFullyLoaded] = useState(false);
    /** True when server returned 200 OK to first range request — Range unsupported. */
    const [rangeUnsupported, setRangeUnsupported] = useState(false);
    const [wordWrap, setWordWrap] = useState(() => {
        try {
            const v = localStorage.getItem('player.text.wordWrap');
            if (v !== null)
                return v !== 'false';
        }
        catch { }
        return initialWordWrap;
    });
    const [lineNumbers, setLineNumbers] = useState(() => {
        try {
            const v = localStorage.getItem('player.text.lineNumbers');
            if (v !== null)
                return v !== 'false';
        }
        catch { }
        return initialLineNumbers;
    });
    const [fontSize, setFontSize] = useState(() => {
        try {
            const v = localStorage.getItem('player.text.fontSize');
            if (v !== null) {
                const n = parseInt(v, 10);
                if (n >= MIN_FONT_SIZE && n <= MAX_FONT_SIZE)
                    return n;
            }
        }
        catch { }
        return DEFAULT_FONT_SIZE;
    });
    const [copyState, setCopyState] = useState('idle');
    // Persist settings to localStorage
    useEffect(() => { try {
        localStorage.setItem('player.text.wordWrap', String(wordWrap));
    }
    catch { } }, [wordWrap]);
    useEffect(() => { try {
        localStorage.setItem('player.text.lineNumbers', String(lineNumbers));
    }
    catch { } }, [lineNumbers]);
    useEffect(() => { try {
        localStorage.setItem('player.text.fontSize', String(fontSize));
    }
    catch { } }, [fontSize]);
    const [selectedLine, setSelectedLineState] = useState(-1);
    const selectedLineRef = useRef(-1);
    const setSelectedLine = useCallback((line) => {
        selectedLineRef.current = line;
        setSelectedLineState(line);
    }, []);
    const [commentMarkers, setCommentMarkers] = useState([]);
    // Fullscreen state
    const [isFullscreen, setIsFullscreen] = useState(false);
    const isFullscreenRef = useRef(false);
    isFullscreenRef.current = isFullscreen;
    // Side panel state
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [sidePanelWidth, setSidePanelWidth] = useState(sidePanelWidthProp ?? 400);
    const [sidePanelHasElement, setSidePanelHasElement] = useState(false);
    const containerRef = useRef(null);
    const scrollerRef = useRef(null);
    const decoderRef = useRef(null);
    const abortRef = useRef(null);
    // Avoid kicking off duplicate fetches on consecutive scroll events.
    const inFlightRef = useRef(false);
    // We track loadedBytes via a ref too so the scroll handler doesn't need
    // to re-bind every render — it always sees the latest value.
    const loadedBytesRef = useRef(0);
    const totalBytesRef = useRef(totalBytes);
    const fullyLoadedRef = useRef(false);
    // Side panel refs
    const sidePanelContentRef = useRef(null);
    const sidePanelAdoptedElRef = useRef(null);
    const sidePanelElementOriginRef = useRef(null);
    useEffect(() => { loadedBytesRef.current = loadedBytes; }, [loadedBytes]);
    useEffect(() => { totalBytesRef.current = totalBytes; }, [totalBytes]);
    useEffect(() => { fullyLoadedRef.current = fullyLoaded; }, [fullyLoaded]);
    // Copy tooltips
    const copyBtnRef = useRef(null);
    const copyTooltipTimeoutRef = useRef(null);
    const copyTooltip = useTooltip(copyBtnRef, {
        trigger: 'manual',
        gravity: 'auto',
        offset: 2,
    });
    useEffect(() => {
        return () => {
            if (copyTooltipTimeoutRef.current != null) {
                window.clearTimeout(copyTooltipTimeoutRef.current);
            }
        };
    }, []);
    const showCopyTooltip = useCallback((message) => {
        if (copyTooltipTimeoutRef.current !== null) {
            window.clearTimeout(copyTooltipTimeoutRef.current);
        }
        copyTooltip.show(message);
        copyTooltipTimeoutRef.current = window.setTimeout(() => {
            copyTooltip.hide();
        }, 1500);
    }, [copyTooltip]);
    // ----------------------------------------------------------------------
    // Range fetcher
    // ----------------------------------------------------------------------
    const fetchRange = useCallback(async (start) => {
        if (inFlightRef.current)
            return;
        if (fullyLoadedRef.current)
            return;
        inFlightRef.current = true;
        setIsLoadingChunk(true);
        const end = start + chunkSize - 1;
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const resp = await fetch(fileUrl, {
                headers: { Range: `bytes=${start}-${end}` },
                credentials: 'include',
                signal: controller.signal,
            });
            // Server doesn't honor Range: a 200 OK on the first request means
            // the entire body is in this single response.
            if (resp.status === 200 && start === 0) {
                setRangeUnsupported(true);
                const buf = new Uint8Array(await resp.arrayBuffer());
                if (!decoderRef.current)
                    decoderRef.current = makeStreamingDecoder();
                const chunkText = decoderRef.current.feed(buf, true);
                setText((prev) => prev + chunkText);
                setLoadedBytes(buf.byteLength);
                setTotalBytes(buf.byteLength);
                setFullyLoaded(true);
                return;
            }
            // 416 Range Not Satisfiable — the file is shorter than we
            // thought. Treat as "loading complete" rather than an error.
            if (resp.status === 416) {
                setTotalBytes(loadedBytesRef.current);
                setFullyLoaded(true);
                return;
            }
            if (!resp.ok && resp.status !== 206) {
                throw new Error(`HTTP ${resp.status}`);
            }
            // Trust the server's Content-Range over the prop fileSize.
            const total = totalBytesFromContentRange(resp.headers.get('Content-Range'));
            if (total != null)
                setTotalBytes(total);
            const buf = new Uint8Array(await resp.arrayBuffer());
            if (buf.byteLength === 0) {
                // Nothing more to read — treat as done.
                setFullyLoaded(true);
                return;
            }
            const newLoaded = start + buf.byteLength;
            const isLast = total != null
                ? newLoaded >= total
                : (totalBytesRef.current > 0 && newLoaded >= totalBytesRef.current);
            if (!decoderRef.current)
                decoderRef.current = makeStreamingDecoder();
            const chunkText = decoderRef.current.feed(buf, isLast);
            setText((prev) => prev + chunkText);
            setLoadedBytes(newLoaded);
            if (isLast)
                setFullyLoaded(true);
        }
        catch (err) {
            if (err && err.name === 'AbortError') {
                // expected on unmount
                return;
            }
            // eslint-disable-next-line no-console
            console.error('Text viewer fetch failed:', err);
            setLoadError(String(err && err.message ? err.message : err));
        }
        finally {
            inFlightRef.current = false;
            setIsLoadingChunk(false);
        }
    }, [fileUrl, chunkSize]);
    // Initial load + cleanup
    useEffect(() => {
        fetchRange(0);
        return () => {
            if (abortRef.current) {
                abortRef.current.abort();
                abortRef.current = null;
            }
        };
        // First-load only — host re-mounts the component when the file changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Scroll-driven incremental loading
    const onScroll = useCallback(() => {
        if (rangeUnsupported || fullyLoadedRef.current || inFlightRef.current)
            return;
        const el = scrollerRef.current;
        if (!el)
            return;
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining < SCROLL_TRIGGER_PX) {
            fetchRange(loadedBytesRef.current);
        }
    }, [fetchRange, rangeUnsupported]);
    // After every text update, check whether the viewport is already short
    // enough that the bottom is visible — if so, immediately request the next
    // chunk so we don't stall when a tiny chunk doesn't fill the viewport.
    useEffect(() => {
        if (rangeUnsupported || fullyLoaded || inFlightRef.current)
            return;
        const el = scrollerRef.current;
        if (!el)
            return;
        if (el.scrollHeight <= el.clientHeight) {
            fetchRange(loadedBytesRef.current);
        }
    }, [text, fullyLoaded, rangeUnsupported, fetchRange]);
    // ----------------------------------------------------------------------
    // Fullscreen handling
    // ----------------------------------------------------------------------
    useEffect(() => {
        const handleChange = () => {
            const isFs = !!getFullscreenElement();
            setIsFullscreen(isFs);
            onFullscreenChange?.(isFs);
            const container = containerRef.current;
            if (container) {
                if (isFs) {
                    container.classList.add(FULLSCREEN_CLASS);
                }
                else {
                    container.classList.remove(FULLSCREEN_CLASS);
                }
            }
        };
        const cleanup = addFullscreenChangeListener(handleChange);
        return () => {
            cleanup();
            containerRef.current?.classList.remove(FULLSCREEN_CLASS);
        };
    }, [onFullscreenChange]);
    const toggleFullscreen = useCallback(() => {
        const container = containerRef.current;
        if (!container)
            return;
        if (getFullscreenElement()) {
            exitFs();
        }
        else {
            requestFullscreen(container);
        }
    }, []);
    // Restore side panel element and close panel when leaving fullscreen
    useEffect(() => {
        if (!isFullscreen) {
            setSidePanelOpen(false);
            const origin = sidePanelElementOriginRef.current;
            const el = sidePanelAdoptedElRef.current;
            if (origin && el) {
                if (origin.nextSibling) {
                    origin.parent.insertBefore(el, origin.nextSibling);
                }
                else {
                    origin.parent.appendChild(el);
                }
                sidePanelElementOriginRef.current = null;
                sidePanelAdoptedElRef.current = null;
            }
        }
    }, [isFullscreen]);
    // Sync sidePanelWidth prop to state
    useEffect(() => {
        if (sidePanelWidthProp != null && sidePanelWidthProp > 0) {
            setSidePanelWidth(sidePanelWidthProp);
        }
    }, [sidePanelWidthProp]);
    // Toggle wrapper class to reserve space for the side panel when open
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        container.style.setProperty('--side-panel-w', `${sidePanelWidth}px`);
        if (isFullscreen && sidePanelOpen) {
            container.classList.add('hekate-player-sidepanel-active');
        }
        else {
            container.classList.remove('hekate-player-sidepanel-active');
        }
    }, [isFullscreen, sidePanelOpen, sidePanelWidth]);
    // Helper: adopt a DOM element into the side panel (or restore the current one)
    const adoptSidePanelElement = useCallback((elementId) => {
        const panel = sidePanelContentRef.current;
        // Restore any previously adopted element first
        const origin = sidePanelElementOriginRef.current;
        const adopted = sidePanelAdoptedElRef.current;
        if (origin && adopted) {
            if (origin.nextSibling) {
                origin.parent.insertBefore(adopted, origin.nextSibling);
            }
            else {
                origin.parent.appendChild(adopted);
            }
            sidePanelElementOriginRef.current = null;
            sidePanelAdoptedElRef.current = null;
        }
        if (!elementId) {
            setSidePanelHasElement(false);
            return;
        }
        setSidePanelHasElement(true);
        if (!panel)
            return;
        const el = document.getElementById(elementId);
        if (!el || !el.parentElement)
            return;
        if (el.parentElement === panel)
            return;
        sidePanelElementOriginRef.current = {
            parent: el.parentElement,
            nextSibling: el.nextSibling,
        };
        sidePanelAdoptedElRef.current = el;
        panel.appendChild(el);
    }, []);
    // Sync sidePanelElementId prop — adopt when entering fullscreen
    useEffect(() => {
        if (isFullscreen) {
            adoptSidePanelElement(sidePanelElementId ?? null);
        }
    }, [sidePanelElementId, isFullscreen, adoptSidePanelElement]);
    // Unmount cleanup — restore any adopted side panel element
    useEffect(() => {
        return () => {
            const origin = sidePanelElementOriginRef.current;
            const el = sidePanelAdoptedElRef.current;
            if (origin && el) {
                if (origin.nextSibling && origin.nextSibling.parentNode === origin.parent) {
                    origin.parent.insertBefore(el, origin.nextSibling);
                }
                else {
                    origin.parent.appendChild(el);
                }
                sidePanelElementOriginRef.current = null;
                sidePanelAdoptedElRef.current = null;
            }
        };
    }, []);
    // ----------------------------------------------------------------------
    // Toolbar actions
    // ----------------------------------------------------------------------
    const onCopy = useCallback(async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                setCopyState('copied');
                showCopyTooltip('Copied');
            }
            else {
                throw new Error('Clipboard API unavailable');
            }
        }
        catch {
            setCopyState('failed');
            showCopyTooltip('Failed');
        }
        setTimeout(() => {
            setCopyState('idle');
        }, 1500);
    }, [text, showCopyTooltip]);
    const incrementFont = useCallback((delta) => {
        setFontSize((prev) => Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, prev + delta)));
    }, []);
    // ── Responsive overflow menu ──
    const containerWidth = useContainerWidth(containerRef);
    const overflowItems = useMemo(() => {
        const items = [];
        if (containerWidth <= 500) {
            items.push({
                id: 'copy',
                type: 'action',
                label: 'Copy',
                onClick: () => onCopy(),
            });
        }
        if (containerWidth <= 400) {
            if (items.length > 0)
                items.push({ type: 'separator', id: 'sep-font' });
            items.push({
                id: 'font-decrease',
                type: 'action',
                label: 'Decrease Font Size',
                onClick: () => incrementFont(-1),
            });
            items.push({
                id: 'font-reset',
                type: 'action',
                label: `Reset Font Size (${fontSize}px)`,
                onClick: () => setFontSize(DEFAULT_FONT_SIZE),
            });
            items.push({
                id: 'font-increase',
                type: 'action',
                label: 'Increase Font Size',
                onClick: () => incrementFont(1),
            });
        }
        if (containerWidth <= 300) {
            if (items.length > 0)
                items.push({ type: 'separator', id: 'sep-toggles' });
            items.push({
                id: 'word-wrap',
                type: 'checkbox',
                label: 'Word Wrap',
                checked: wordWrap,
                onClick: () => setWordWrap((v) => !v),
            });
            items.push({
                id: 'line-numbers',
                type: 'checkbox',
                label: 'Line Numbers',
                checked: lineNumbers,
                onClick: () => setLineNumbers((v) => !v),
            });
        }
        return items;
    }, [containerWidth, fontSize, wordWrap, lineNumbers, onCopy, incrementFont]);
    // Split text into individual lines for per-line rendering.
    const lines = useMemo(() => {
        const arr = text.split('\n');
        if (arr.length > 1 && arr[arr.length - 1] === '' && text.endsWith('\n'))
            arr.pop();
        return arr;
    }, [text]);
    const lineCount = lines.length;
    // Build a set of lines that have comment markers for O(1) lookup.
    const markersByLine = useMemo(() => {
        const map = new Map();
        for (const m of commentMarkers) {
            // Keep only one marker per line (first wins — typically the most recent)
            if (!map.has(m.line))
                map.set(m.line, m);
        }
        return map;
    }, [commentMarkers]);
    // ----------------------------------------------------------------------
    // Imperative API
    // ----------------------------------------------------------------------
    const lineHeight = fontSize * 1.4;
    const { visibleRange, topSpacerHeight, bottomSpacerHeight, measureLine, getLineOffset, getTopVisibleLineIndex, invalidate: invalidateVirtual, } = useVirtualLines({
        lineCount,
        scrollerRef,
        estimatedLineHeight: lineHeight,
    });
    // Invalidate line height measurements when font size or word wrap changes,
    // and preserve the logical scroll position across the change. Without
    // this, scrollTop (in pixels) stays put while line heights change, so
    // shrinking the font rolls the viewport "downward" through the document
    // and growing the font rolls it "upward". Skip the very first run — there
    // is nothing to preserve at mount, and we don't want to clobber an
    // externally-set scroll position before the user has interacted.
    const didMountRef = useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        const topLineIdx = getTopVisibleLineIndex();
        invalidateVirtual();
        const el = scrollerRef.current;
        if (!el)
            return;
        // After invalidate, `getLineOffset` reads from a freshly-cleared
        // measurement map and returns the offset using the new estimated line
        // height — i.e. the same coordinate space scrollTop now lives in.
        el.scrollTop = Math.max(0, getLineOffset(topLineIdx));
    }, [fontSize, wordWrap, getTopVisibleLineIndex, invalidateVirtual, getLineOffset]);
    // Keep the latest getLineOffset in a ref so `scrollToLine` can stay
    // stable. Without this, `scrollToLine` (and therefore the imperative API
    // object built by `useImperativeHandle`) would be recreated whenever the
    // measured line heights change — and any consumer that captured the API
    // by value at mount time (e.g. PlayerSlot's onViewerReady, which fires
    // once with `api: textViewerRef.current`) would keep calling a stale
    // closure that still sees `lineCount = 0` and scrolls to the top.
    const getLineOffsetRef = useRef(getLineOffset);
    useEffect(() => { getLineOffsetRef.current = getLineOffset; }, [getLineOffset]);
    const scrollToLine = useCallback((line) => {
        const el = scrollerRef.current;
        if (!el)
            return;
        el.scrollTop = Math.max(0, getLineOffsetRef.current(line - 1));
    }, []);
    // Keyboard shortcuts
    useEffect(() => {
        const el = containerRef.current;
        if (!el)
            return;
        const LINES_PER_PAGE = 20;
        const onKeyDown = (e) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
                return;
            // Let Shift+arrow through for native text selection
            if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End'))
                return;
            switch (e.key) {
                case 'ArrowUp': {
                    e.preventDefault();
                    e.stopPropagation();
                    const upLine = Math.max(1, selectedLineRef.current <= 0 ? 1 : selectedLineRef.current - 1);
                    setSelectedLine(upLine);
                    scrollSelectedIntoView(upLine);
                    break;
                }
                case 'ArrowDown': {
                    e.preventDefault();
                    e.stopPropagation();
                    const downLine = Math.min(lineCount, selectedLineRef.current <= 0 ? 1 : selectedLineRef.current + 1);
                    setSelectedLine(downLine);
                    scrollSelectedIntoView(downLine);
                    break;
                }
                case 'Home':
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedLine(1);
                    scrollToLine(1);
                    break;
                case 'End':
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedLine(lineCount);
                    scrollToLine(lineCount);
                    break;
                case 'PageUp': {
                    e.preventDefault();
                    e.stopPropagation();
                    const puLine = Math.max(1, selectedLineRef.current <= 0 ? 1 : selectedLineRef.current - LINES_PER_PAGE);
                    setSelectedLine(puLine);
                    scrollToLine(puLine);
                    break;
                }
                case 'PageDown': {
                    e.preventDefault();
                    e.stopPropagation();
                    const pdLine = Math.min(lineCount, selectedLineRef.current <= 0 ? 1 : selectedLineRef.current + LINES_PER_PAGE);
                    setSelectedLine(pdLine);
                    scrollToLine(pdLine);
                    break;
                }
                case '-':
                    e.preventDefault();
                    incrementFont(-1);
                    break;
                case '=':
                    e.preventDefault();
                    incrementFont(1);
                    break;
                case 'F':
                case 'f':
                    if (e.shiftKey && !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                        toggleFullscreen();
                    }
                    break;
            }
        };
        el.addEventListener('keydown', onKeyDown);
        return () => el.removeEventListener('keydown', onKeyDown);
    }, [lineCount, setSelectedLine, scrollToLine, incrementFont, toggleFullscreen]);
    /** Scroll a line into view using the DOM element if it exists, else scrollToLine. */
    const scrollSelectedIntoView = useCallback((line) => {
        const scroller = scrollerRef.current;
        if (!scroller)
            return;
        const row = scroller.querySelector(`[data-line="${line}"]`);
        if (row) {
            // Only scroll if the row is outside the visible area
            const scrollerRect = scroller.getBoundingClientRect();
            const rowRect = row.getBoundingClientRect();
            if (rowRect.top < scrollerRect.top) {
                row.scrollIntoView({ block: 'start' });
            }
            else if (rowRect.bottom > scrollerRect.bottom) {
                row.scrollIntoView({ block: 'end' });
            }
        }
        else {
            scrollToLine(line);
        }
    }, [scrollToLine]);
    const handleLineClick = useCallback((line) => {
        setSelectedLine(selectedLineRef.current === line ? -1 : line);
    }, [setSelectedLine]);
    // The imperative API object must stay stable across renders: PlayerSlot
    // captures `textViewerRef.current` once on mount and stores it in the
    // column manager's `latestPlayerHandle.api`, so any later recreation of
    // this object would leave that captured reference pointing at a stale
    // closure (e.g. one whose `scrollToLine` still sees `lineCount = 0`).
    // Every method here reads mutable state via refs so the stable closure
    // always sees the latest values.
    const adoptSidePanelElementRef = useRef(adoptSidePanelElement);
    useEffect(() => { adoptSidePanelElementRef.current = adoptSidePanelElement; }, [adoptSidePanelElement]);
    useImperativeHandle(ref, () => ({
        scrollToLine,
        getLoadedBytes: () => loadedBytesRef.current,
        getTotalBytes: () => totalBytesRef.current,
        isFullscreen: () => isFullscreenRef.current,
        enterFullscreen: () => {
            const container = containerRef.current;
            if (container && !getFullscreenElement()) {
                requestFullscreen(container);
            }
        },
        exitFullscreen: () => {
            if (getFullscreenElement())
                exitFs();
        },
        setSidePanelElement: (elementId) => {
            adoptSidePanelElementRef.current(elementId);
        },
        setSidePanelOpen: (open) => { setSidePanelOpen(open); },
        setSidePanelWidth: (width) => { setSidePanelWidth(width); },
        getContainerElement: () => containerRef.current,
        getKeyboardShortcuts: () => TEXT_VIEWER_KEYBOARD_SHORTCUTS,
        focus: (options) => { containerRef.current?.focus(options); },
        getCurrentLine: () => selectedLineRef.current,
        setCurrentLine: (line) => {
            setSelectedLine(line);
            if (line > 0)
                scrollToLine(line);
        },
        updateCommentMarkers: (markers) => { setCommentMarkers(markers); },
    }), [scrollToLine, setSelectedLine]);
    // ----------------------------------------------------------------------
    // ----------------------------------------------------------------------
    // Render
    // ----------------------------------------------------------------------
    if (loadError) {
        return (_jsx(DownloadOverlay, { visible: true, message: "This file could not be loaded.", canDownload: !!downloadUrl, downloadUrl: downloadUrl || undefined, title: fileTitle, fileTypeName: fileTypeName || 'Text File', fileSize: totalBytes || fileSize, fileIconUrl: fileIconUrl }));
    }
    // Outer container — flex column, fills the parent stage.
    const outerStyle = {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        position: 'relative',
        outline: 'none',
    };
    // Fill the parent stage (set by PlayerSlot's NON_MEDIA_STAGE_STYLE to
    // a 16:9 box sized from the column width). The scroller inside uses
    // flex:1 1 auto to take the remaining vertical space after the toolbar.
    const containerStyle = {
        width: '100%',
        height: '100%',
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        border: 'thin solid #373737',
    };
    const toolbarStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        backgroundColor: 'var(--player-control-container-bg-color)',
        fontSize: 12,
        flexShrink: 0,
        color: '#fff',
    };
    const toolbarBtnStyle = {
        color: '#fff',
        backgroundColor: 'transparent',
        border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: 3,
        padding: '2px 8px',
        cursor: 'pointer',
        fontSize: 12,
    };
    const scrollerStyle = {
        flex: '1 1 auto',
        overflow: 'auto',
        backgroundColor: '#fff',
    };
    const progressLabel = (() => {
        if (totalBytes <= 0)
            return formatFileSize(loadedBytes);
        if (fullyLoaded)
            return `${formatFileSize(totalBytes)} (loaded)`;
        return `${formatFileSize(loadedBytes)} / ${formatFileSize(totalBytes)}`;
    })();
    return (_jsxs("div", { ref: containerRef, className: "hekate-text-viewer-wrapper", style: outerStyle, tabIndex: -1, children: [isFullscreen && containerRef.current && createPortal(_jsx(SidePanel, { isOpen: sidePanelOpen, onToggle: () => setSidePanelOpen((v) => !v), controlsVisible: true, contentRef: sidePanelContentRef, hasElement: sidePanelHasElement, tooltip: sidePanelTooltip }), containerRef.current), _jsxs("div", { className: "hekate-text-viewer", style: containerStyle, children: [_jsxs("div", { className: "hekate-text-viewer-scroller", ref: scrollerRef, onScroll: onScroll, style: scrollerStyle, children: [_jsx("div", { style: { height: topSpacerHeight, flexShrink: 0 } }), lines.slice(visibleRange.start, visibleRange.end).map((lineText, i) => {
                                const lineIdx = visibleRange.start + i;
                                const line = lineIdx + 1;
                                const isSelected = line === selectedLine;
                                const marker = markersByLine.get(line);
                                const hasAnyMarkers = markersByLine.size > 0;
                                const markerH = Math.round(lineHeight * 0.75);
                                const markerW = Math.round(markerH * (10 / 12));
                                return (_jsxs("div", { ref: (el) => { if (el)
                                        measureLine(lineIdx, el.offsetHeight); }, className: `hekate-text-viewer-row${isSelected ? ' hekate-text-viewer-row-selected' : ''}`, "data-line": line, onClick: () => handleLineClick(line), style: { fontSize, lineHeight: 1.4, fontFamily: 'Menlo, Consolas, Monaco, monospace' }, children: [lineNumbers && (_jsxs("div", { className: "hekate-text-viewer-gutter-cell", children: [marker ? (_jsx("span", { className: "hekate-text-viewer-comment-marker", role: "button", tabIndex: -1, "aria-label": `Comment on line ${line}`, style: {
                                                        width: markerW,
                                                        height: markerH,
                                                        backgroundImage: getMarkerFillUri(marker.color, markerW, markerH, true),
                                                    }, onClick: (e) => {
                                                        e.stopPropagation();
                                                        if (typeof window.scrollToCommentID === 'function') {
                                                            window.scrollToCommentID(marker.id);
                                                        }
                                                    } })) : hasAnyMarkers && (_jsx("span", { className: "hekate-text-viewer-marker-spacer", style: { width: markerW, height: markerH } })), _jsx("span", { className: "hekate-text-viewer-line-number", children: line })] })), _jsx("div", { className: "hekate-text-viewer-content-cell", style: {
                                                whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
                                                wordBreak: wordWrap ? 'break-word' : 'normal',
                                            }, children: lineText || '\u200b' })] }, lineIdx));
                            }), _jsx("div", { style: { height: bottomSpacerHeight, flexShrink: 0 } })] }), _jsxs("div", { className: "hekate-text-viewer-toolbar", style: toolbarStyle, children: [_jsx("button", { type: "button", ref: copyBtnRef, className: "hekate-player-btn hekate-tv-collapsible hekate-tv-copy", style: toolbarBtnStyle, title: "Copy selected text to clipboard", "aria-label": "Copy selected text to clipboard", onClick: onCopy, children: _jsx("div", { className: "fa-icon-clone-outline" }) }), _jsxs("span", { className: "hekate-tv-collapsible hekate-tv-toggles", children: [_jsx("check-box", { checked: wordWrap, text: "Word Wrap", style: { marginRight: '5px' }, onChange: (e) => setWordWrap(e.target.checked) }), _jsx("check-box", { checked: lineNumbers, text: "Line Numbers", onChange: (e) => setLineNumbers(e.target.checked) })] }), _jsxs("span", { className: "hekate-tv-collapsible hekate-tv-font-size", style: { display: 'flex', alignItems: 'center', gap: 4 }, children: [_jsx("button", { type: "button", className: "hekate-player-btn hekate-image-zoom-out-btn", style: toolbarBtnStyle, onClick: () => incrementFont(-1), "aria-label": "Decrease font size", children: _jsx("span", { className: "hekate-player-btn-icon", "aria-hidden": "true" }) }), _jsxs("button", { type: "button", id: "text-viewer-font-size-btn", style: toolbarBtnStyle, onClick: () => setFontSize(DEFAULT_FONT_SIZE), "aria-label": "Reset font size", children: [fontSize, "px"] }), _jsx("button", { type: "button", className: "hekate-player-btn hekate-image-zoom-in-btn", style: toolbarBtnStyle, onClick: () => incrementFont(1), "aria-label": "Increase font size", children: _jsx("span", { className: "hekate-player-btn-icon", "aria-hidden": "true" }) })] }), _jsx("span", { className: "hekate-tv-overflow-btn", children: _jsx(ViewerToolbarOverflowMenu, { items: overflowItems }) }), _jsx("div", { className: "spacer" }), _jsxs("span", { className: "hekate-tv-progress", title: progressLabel + (isLoadingChunk && !fullyLoaded ? ' — loading…' : ''), children: [progressLabel, isLoadingChunk && !fullyLoaded ? ' — loading…' : ''] }), _jsx(FullscreenButton, { isFullscreen: isFullscreen, onClick: toggleFullscreen })] })] })] }));
});
export default HekateTextViewer;
